// Файл use_document_attachments.js

/**
 * Модуль «use document attachments» содержит логику, относящуюся к разделу «document attachments parts».
 * Он объединяет связанные обработчики, данные или элементы интерфейса и предоставляет их соседним модулям приложения.
 * Такое разделение упрощает сопровождение, тестирование и повторное использование этой части кода.
 */

import { useEffect, useState } from 'react';

import { apiService } from '../../../services/api.js';
import { isViewableInPanel } from '../FileViewer';



const use_document_attachments = ({
    ID_document,
    allow_download,
    displayNameOnly,
    downloadFile,
    errorText,
    extraParams,
    filesOverride,
    filesPdfPathsOverride,
    loadFileBlob,
    refreshKey,
    showToast,
    tableName,
}) => {
    const [files, setFiles] = useState(null);
    const [filesPdfPaths, setFilesPdfPaths] = useState({});
    const [error, setError] = useState(null);
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const displayed_files = Array.isArray(filesOverride) ? filesOverride : files;
    const displayed_pdf_paths = filesPdfPathsOverride || filesPdfPaths;

    useEffect(() => {
        if (Array.isArray(filesOverride)) {
            setError(null);
            return;
        }

        const is_pay_resident = tableName === 'get_pay_resident';
        const params = is_pay_resident
            ? {
                var_6: extraParams?.var_6,
                period: extraParams?.period,
                ID_house: extraParams?.ID_house,
                view_operation: extraParams?.view_operation,
                file_name: extraParams?.file_name,
            }
            : extraParams
                ? {
                    id: ID_document,
                    doc: ID_document,
                    ...extraParams,
                }
                : ID_document;

        apiService.df({ table_name: tableName, params })
            .then((response) => {
                const list = response?.files ?? null;
                setFiles(Array.isArray(list) ? list : []);
                setFilesPdfPaths(response?.files_pdf_paths ?? {});
            })
            .catch(() => setError(errorText));
    }, [
        ID_document,
        errorText,
        extraParams?.ID_house,
        extraParams?.file_name,
        extraParams?.period,
        extraParams?.var_6,
        extraParams?.view_operation,
        filesOverride,
        refreshKey,
        tableName,
    ]);

    const display_file_name = (file_name) => {
        if (!displayNameOnly) {
            return file_name;
        }

        return String(file_name || '').replace(/\\/g, '/').split('/').pop();
    };

    const download_file = async (event, file_name) => {
        event.preventDefault();
        event.stopPropagation();

        if (!allow_download) {
            return;
        }

        try {
            if (typeof downloadFile === 'function') {
                await downloadFile(file_name);

            } else {
                await apiService.download_file(
                    tableName,
                    file_name,
                    {
                        doc: ID_document,
                        file: file_name,
                        ...(extraParams || {}),
                    },
                );
            }

        } catch {
            showToast(`Не удалось скачать файл: ${file_name}`, 'error');
        }
    };

    const open_file = (file_name) => {
        const ext = file_name.split('.').pop()?.toLowerCase() || '';
        const selected = {
            name: file_name,
            display_name: display_file_name(file_name),
            ext,
        };

        if (ext === 'pdf') {
            selected.pdf_path = displayed_pdf_paths?.[file_name];

            if (!selected.pdf_path && typeof loadFileBlob !== 'function') {
                showToast(`Не найден путь к PDF: ${file_name}`, 'error');
                return;
            }

        } else if (!isViewableInPanel(ext)) {
            if (!allow_download) {
                return;
            }

            download_file(
                {
                    preventDefault: () => {},
                    stopPropagation: () => {},
                },
                file_name,
            );
            return;
        }

        setSelectedFile(selected);
        setIsViewerOpen(true);
    };

    return {
        displayed_files,
        download_file,
        error,
        isViewerOpen,
        open_file,
        selectedFile,
        setIsViewerOpen,
    };
};


export default use_document_attachments;
